import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import DropDown from './DropDown';
import BrochureMenu from './BrochureMenu';
import '@fortawesome/fontawesome-free/css/all.min.css';
import brandLogo from '../Images/dochoice-m1.png';

const productLinks = [
  { title: 'Hospital Management System', href: '/products#hms' },
  { title: 'Laboratory Information System (LIS)', href: '/products#lis' },
  { title: 'Pharmacy Management', href: '/products#pharmacy' },
  { title: 'HR & Payroll', href: '/products#hr' },
  { title: 'Finance Management', href: '/products#finance' },
  { title: 'Machine Integration', href: '/products#machine-integration' },
  { title: 'Cloud & Patient Services', href: '/products#cloud-services' },
];

const moduleLinks = [
  { title: 'OP Management', href: '/modules#op-management' },
  { title: 'IP Management', href: '/modules#ip-management' },
  { title: 'Laboratory', href: '/modules#laboratory' },
  { title: 'Pharmacy', href: '/modules#pharmacy' },
  { title: 'Inventory', href: '/modules#inventory' },
  { title: 'Finance', href: '/modules#finance' },
  { title: 'HR Management', href: '/modules#hr-management' },
  { title: 'ABDM', href: '/modules#abdm' },
  { title: 'Patient Portal', href: '/modules#patient-portal' },
  {
    title: 'IVR Appointment Management',
    href: '/modules#ivr-appointment-management',
  },
  {
    title: 'WhatsApp Patient Communication',
    href: '/modules#whatsapp-patient-communication',
  },
];

function NavDropdown({ label, links, isActive, onNavigate, to, resetKey }) {
  const closeDropdownFocus = () => {
    if (typeof document === 'undefined') return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleNavigate = () => {
    closeDropdownFocus();
    onNavigate?.();
  };

  return (
    <li
      className={`menu-item-has-children${isActive ? ' current-menu-item' : ''}`}
      onMouseLeave={closeDropdownFocus}
    >
      {to ? (
        <Link to={to} className="cs_nav_label" onClick={handleNavigate}>
          {label}
          <Icon icon="lucide:chevron-down" width={14} height={14} aria-hidden="true" />
        </Link>
      ) : (
        <span className="cs_nav_label">
          {label}
          <Icon icon="lucide:chevron-down" width={14} height={14} aria-hidden="true" />
        </span>
      )}
      <DropDown resetKey={resetKey}>
        <ul className="cs_header_dropdown">
          {links.map((item) => (
            <li key={item.title}>
              <Link to={item.href} onClick={handleNavigate}>
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      </DropDown>
    </li>
  );
}

export default function Header({ variant }) {
  const [isSticky, setIsSticky] = useState(false);
  const [mobileToggle, setMobileToggle] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileToggle(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 8);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileToggle(false);
  const dropdownResetKey = `${location.pathname}${location.hash}`;
  const path = location.pathname;
  const isHome = path === '/';
  const isProductsActive =
    path === '/products' || path.startsWith('/products/');
  const isModulesActive =
    path === '/modules' || path.startsWith('/modules/');
  const isAboutActive =
    path === '/about' || path === '/careers' || path.startsWith('/about');
  const isContactActive = path === '/contact';

  return (
    <header
      className={`cs_site_header cs_style1 cs_sticky_header cs_header_premium ${
        mobileToggle ? 'cs_mobile_toggle_active' : ''
      } ${variant || ''} ${isSticky ? 'cs_active_sticky' : ''}`}
    >
      <div className="cs_main_header">
        <div className="container">
          <div className="cs_main_header_in">
            <div className="cs_main_header_left">
              <Link className="cs_site_branding" to="/" onClick={closeMobile}>
                <img
                  src={brandLogo}
                  alt="DocChoice by Moka Softwares"
                  className="cs_header_logo_img"
                  decoding="async"
                />
                <span className="cs_header_brand_copy">
                  <span className="cs_header_brand_name" aria-label="DocChoice">
                    <span className="cs_header_brand_doc">Doc</span>
                    <span className="cs_header_brand_choice">Choice</span>
                  </span>
                  <span className="cs_header_brand_tagline">
                    <span className="cs_header_brand_tagline_doc">
                      Empowering Healthcare
                    </span>
                    <span className="cs_header_brand_tagline_sep"> – </span>
                    <span className="cs_header_brand_tagline_choice">
                      Enrich Lives
                    </span>
                  </span>
                </span>
              </Link>
            </div>

            <div className="cs_main_header_center">
              <nav className="cs_nav">
                <ul
                  className={
                    mobileToggle ? 'cs_nav_list cs_active' : 'cs_nav_list'
                  }
                >
                  <li className={isHome ? 'current-menu-item' : ''}>
                    <Link to="/" onClick={closeMobile}>
                      Home
                    </Link>
                  </li>

                  <NavDropdown
                    label="Products"
                    to="/products"
                    links={productLinks}
                    isActive={isProductsActive}
                    onNavigate={closeMobile}
                    resetKey={dropdownResetKey}
                  />

                  <NavDropdown
                    label="Modules"
                    to="/modules"
                    links={moduleLinks}
                    isActive={isModulesActive}
                    onNavigate={closeMobile}
                    resetKey={dropdownResetKey}
                  />

                  <li className={isAboutActive ? 'current-menu-item' : ''}>
                    <Link to="/about" onClick={closeMobile}>
                      About Us
                    </Link>
                  </li>

                  <li className={isContactActive ? 'current-menu-item' : ''}>
                    <Link to="/contact" onClick={closeMobile}>
                      Contact
                    </Link>
                  </li>

                  <li className="cs_nav_demo_mobile">
                    <BrochureMenu
                      menuId="cs-brochure-menu-mobile"
                      onDownload={closeMobile}
                      resetKey={`${dropdownResetKey}-${mobileToggle}`}
                    />
                  </li>

                  <li className="cs_nav_demo_mobile">
                    <Link
                      to="/appointments"
                      className="cs_header_demo_btn"
                      onClick={closeMobile}
                    >
                      Book a Free Demo
                      <Icon icon="lucide:arrow-right" width={16} height={16} />
                    </Link>
                  </li>
                </ul>

                <span
                  className={
                    mobileToggle
                    ? 'cs_menu_toggle cs_toggle_active'
                    : 'cs_menu_toggle'
                  }
                  onClick={() => setMobileToggle(!mobileToggle)}
                  role="button"
                  tabIndex={0}
                  aria-label="Toggle menu"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setMobileToggle(!mobileToggle);
                    }
                  }}
                >
                  <span />
                </span>
              </nav>
            </div>

            <div className="cs_main_header_right">
              <BrochureMenu menuId="cs-brochure-menu-desktop" />
              <Link to="/appointments" className="cs_header_demo_btn">
                Book a Free Demo
                <Icon icon="lucide:arrow-right" width={16} height={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
